import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../Input';
import { useLazyGetAreasQuery } from 'app/api/marine-infrastructure';
import { throttle, groupBy } from 'app/helpers';
import { Icon } from '../Icon';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { getI18n, useTranslation } from 'react-i18next';
import { useOnClickOutside, useZoneSelectHandler } from 'app/hooks';
import { AreaForm } from '../AreaForm';
import { Modal } from '../Modal';
import { Area, AreaInfo } from 'ais-facade-api/api';

import styles from './AreasList.module.scss';

const sortCb = (prev: AreaInfo, cur: AreaInfo) => {
  if (!prev.name || !cur.name) return 0;
  if (prev.name.toLowerCase() < cur.name.toLowerCase()) {
    return -1;
  }
  if (prev.name.toLowerCase() > cur.name.toLowerCase()) {
    return 1;
  }
  return 0;
};

const sortSubtypeCb = (prev: AreaInfo, cur: AreaInfo) => {
  if (!prev.subType || !cur.subType) return 0;
  if (prev.subType.toLowerCase() < cur.subType.toLowerCase()) {
    return -1;
  }
  if (prev.subType.toLowerCase() > cur.subType.toLowerCase()) {
    return 1;
  }
  return 0;
};

export const AreasList = () => {
  const { t } = useTranslation();
  const handleZoneSelect = useZoneSelectHandler();
  const [getAreas, { data, ...getAreasRequest }] = useLazyGetAreasQuery();
  const debouncedGetAreasHandler = useCallback(throttle(getAreas, 700), []);
  const language = getI18n().language;

  const selectedArea = useAppSelector((state) => state.marineInfrastructureSlice.selectedArea);

  useEffect(() => {
    debouncedGetAreasHandler({});
  }, [language]);

  const [inputValue, setInputValue] = useState<string>('');
  const [areaListCommon, setAreaListCommon] = useState<AreaInfo[] | null>();
  const [areaListUser, setAreaListUser] = useState<AreaInfo[] | null>();
  const [areaListSpecial, setAreaListSpecial] = useState<{
    [key: string]: Area[];
  } | null>();

  useEffect(() => {
    if (data?.results && data?.results?.length > 0) {
      const commonlist = [];
      const userlist = [];
      const specialList = [];

      for (let i = 0; i < data?.results?.length; i++) {
        const area = data.results[i];

        if (area.type === 'COMMON') {
          if ('subType' in area) {
            specialList.push(area);
          } else {
            commonlist.push(area);
          }
        } else if (area.type === 'USER') {
          userlist.push(area);
        }
      }

      const grouppedSpecialArea: Area[] = specialList.map((area) => {
        return { ...area };
      });
      const areasGrouped = groupBy(grouppedSpecialArea, ({ subType }: Area) => subType as string);
      const areasKeys = Object.keys(areasGrouped).sort((a, b) => {
        const res = a.localeCompare(b);
        return res;
      });
      areasKeys.forEach((key) => {
        areasGrouped[key].sort(sortCb).sort(sortSubtypeCb);
      });

      setAreaListSpecial(areasGrouped);
      setAreaListCommon(commonlist.sort(sortCb));
      setAreaListUser(userlist.sort(sortCb));
    }
  }, [data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const inputValue: string = e.target.value.toLowerCase();

    const filteredCommon =
      data?.results
        ?.filter((value) => value.name?.toLowerCase().includes(inputValue) && value.type === 'COMMON' && !('subType' in value))
        ?.sort(sortCb) || null;
    const filteredUser =
      data?.results?.filter((value) => value?.name?.toLowerCase().includes(inputValue) && value.type === 'USER')?.sort(sortCb) ||
      null;
    const filteredSpecial =
      data?.results?.filter((value) => value?.name?.toLowerCase().includes(inputValue) && 'subType' in value) || null;

    if (filteredSpecial) {
      const grouppedSpecialArea: Area[] = filteredSpecial.map((area) => {
        return { ...area };
      });
      const areasGrouped = groupBy(grouppedSpecialArea, ({ subType }: Area) => subType as string);
      const areasKeys = Object.keys(areasGrouped);
      areasKeys.forEach((key) => {
        areasGrouped[key].sort(sortCb).sort(sortSubtypeCb);
      });
      setAreaListSpecial(areasGrouped);
    } else {
      setAreaListSpecial(null);
    }

    setAreaListCommon(filteredCommon);
    setAreaListUser(filteredUser);
  };

  const areaListItemsCommon = useMemo(() => {
    return areaListCommon?.map((area) => {
      return (
        <li
          key={area.code}
          className={styles.areaName}
          onClick={() => {
            handleZoneSelect(area);
          }}
          aria-selected={selectedArea?.code === area.code}
        >
          {area.name}
        </li>
      );
    });
  }, [areaListCommon, selectedArea]);

  const areaListItemsUser = useMemo(
    () =>
      areaListUser?.map((area) => {
        return (
          <li
            key={area.code}
            className={styles.areaName}
            onClick={() => {
              handleZoneSelect(area);
            }}
            aria-selected={selectedArea?.code === area.code}
          >
            {area.name}
          </li>
        );
      }),
    [areaListUser, selectedArea],
  );

  const areasSpecial = useMemo(() => {
    if (areaListSpecial) {
      const areaSubtypes = Object.keys(areaListSpecial).sort((a, b) => a.localeCompare(b));

      return areaSubtypes.map((areaSubtype) => {
        return (
          <li key={areaSubtype} className={styles.areaName}>
            <details open className={styles.details}>
              <summary className={styles.summary}>
                <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                <span className={styles.list_titles}> {areaSubtype}</span>
              </summary>
              <ul className={styles.list}>
                {areaListSpecial[areaSubtype].map((area) => (
                  <li
                    key={area.code}
                    className={styles.areaName}
                    onClick={() => {
                      handleZoneSelect(area);
                    }}
                    aria-selected={selectedArea?.code === area.code}
                  >
                    {area.name}
                  </li>
                ))}
              </ul>
            </details>
          </li>
        );
      });
    }
  }, [areaListSpecial, selectedArea]);

  const areaIsLoading = getAreasRequest.isLoading || getAreasRequest.isFetching;
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const createModalRef = useRef<HTMLDivElement>(null);

  const clickOutsideHandler = () => {
    setShowCreateModal(false);
  };

  useOnClickOutside(createModalRef, clickOutsideHandler);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.heading}>
          <Icon name="catalog-areas" className={styles.headingIcon} />
          <span>{t('Areas')}</span>
        </div>
        {areaIsLoading && <span className={styles.loading}>{t('SearchingText')}</span>}
        {!areaIsLoading && data?.results && data.results.length > 0 && (
          <>
            <Input
              type="search"
              onChange={handleInputChange}
              placeholder={t('SearchPlaceholder')}
              className={styles.input}
              value={inputValue}
            />

            <ul className={styles.listUp}>
              <li>
                <details open className={styles.details}>
                  <summary className={styles.summary}>
                    <Icon name="chevron-down-small" size="14px" className={styles.arrow} />

                    <span className={styles.list_titles}>{t('UserAreasTitle')}</span>
                  </summary>
                  {areaListItemsUser && areaListItemsUser?.length > 0 ? (
                    <ul className={styles.list}>{areaListItemsUser}</ul>
                  ) : null}
                </details>
              </li>
              <li>
                <details open className={styles.details}>
                  <summary className={styles.summary}>
                    <Icon name="chevron-down-small" size="14px" className={styles.arrow} />

                    <span className={styles.list_titles}>{t('SpecialAreasTitle')}</span>
                  </summary>
                  {areasSpecial && areasSpecial?.length > 0 ? <ul className={styles.list}>{areasSpecial}</ul> : null}
                </details>
              </li>
              <li>
                <details open className={styles.details}>
                  <summary className={styles.summary}>
                    <Icon name="chevron-down-small" size="14px" className={styles.arrow} />

                    <span className={styles.list_titles}>{t('CommonAreasTitle')}</span>
                  </summary>
                  {areaListItemsCommon && areaListItemsCommon?.length > 0 ? (
                    <ul className={styles.list}>{areaListItemsCommon}</ul>
                  ) : null}
                </details>
              </li>
            </ul>
          </>
        )}
        <div className={styles.actions}>
          <div
            title={t('NewGeofence')}
            onClick={() => {
              setShowCreateModal(true);
            }}
          >
            <Icon name="plus" color="var(--default-primary)" />
          </div>
        </div>

        <Modal
          isShown={showCreateModal}
          onClose={clickOutsideHandler}
          component={
            <AreaForm
              mode="CREATE"
              onClose={clickOutsideHandler}
              onSuccess={(area: AreaInfo) => {
                debouncedGetAreasHandler({});
                handleZoneSelect(area);
              }}
            />
          }
          headerTitle={t('NewGeofence')}
        />
      </div>
    </>
  );
};
